import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getClassStats } from "../lib/api";
import { BarChart3, TrendingUp, Users, Award, Clock } from "lucide-react";

export default function ClassDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === "teacher") {
      getClassStats()
        .then((res) => setStats(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  if (user?.role !== "teacher") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center page-enter">
        <img src="/LEcoleDeNavi5.png" alt="Navi" className="h-32 mx-auto mb-4 opacity-50" />
        <p className="font-nunito text-slate-400">Espace reserve aux enseignants.</p>
      </div>
    );
  }

  const students = stats?.students || [];
  const maxWpm = Math.max(...students.map((s) => s.avg_wpm), 1);

  return (
    <div data-testid="class-dashboard" className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 page-enter has-bottom-nav lg:pb-8">
      <div className="flex items-center gap-3 mb-8">
        <BarChart3 size={28} className="text-blue-500" />
        <h1 data-testid="class-title" className="font-fredoka text-3xl md:text-4xl font-bold text-slate-800">
          Tableau de bord classe
        </h1>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <img src="/LEcoleDeNavi4.png" alt="Chargement" className="h-16 mx-auto mascot-float" />
        </div>
      ) : students.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
          <Users size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="font-nunito text-slate-400">Aucun eleve avec des sessions de lecture.</p>
          <p className="font-nunito text-sm text-slate-400 mt-1">Les donnees apparaitront quand vos eleves liront des textes.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-4 text-center shadow-sm">
              <Users size={20} className="text-blue-500 mx-auto mb-1" />
              <p className="font-fredoka font-bold text-2xl text-blue-700">{students.length}</p>
              <p className="font-nunito text-[10px] text-slate-400 font-bold uppercase">Eleves actifs</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-4 text-center shadow-sm">
              <Award size={20} className="text-orange-500 mx-auto mb-1" />
              <p className="font-fredoka font-bold text-2xl text-orange-700">
                {students.reduce((s, st) => s + st.total_words, 0)}
              </p>
              <p className="font-nunito text-[10px] text-slate-400 font-bold uppercase">Mots lus</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-4 text-center shadow-sm">
              <Clock size={20} className="text-green-500 mx-auto mb-1" />
              <p className="font-fredoka font-bold text-2xl text-green-700">
                {Math.round(students.reduce((s, st) => s + st.total_time_seconds, 0) / 60)}
              </p>
              <p className="font-nunito text-[10px] text-slate-400 font-bold uppercase">Min totales</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-4 text-center shadow-sm">
              <TrendingUp size={20} className="text-purple-500 mx-auto mb-1" />
              <p className="font-fredoka font-bold text-2xl text-purple-700">
                {students.length > 0 ? Math.round(students.reduce((s, st) => s + st.avg_wpm, 0) / students.length) : 0}
              </p>
              <p className="font-nunito text-[10px] text-slate-400 font-bold uppercase">Mots/min moy.</p>
            </div>
          </div>

          {/* Bar chart - Words per minute by student */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 md:p-6 shadow-md">
            <h2 className="font-fredoka font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
              <BarChart3 size={18} className="text-blue-500" />
              Vitesse de lecture (mots/minute)
            </h2>
            <div className="space-y-3">
              {students.sort((a, b) => b.avg_wpm - a.avg_wpm).map((student) => {
                const pct = (student.avg_wpm / maxWpm) * 100;
                return (
                  <div key={student.id} data-testid={`chart-bar-${student.id}`} className="flex items-center gap-3">
                    <div className="w-20 md:w-28 flex-shrink-0">
                      <p className="font-nunito font-bold text-sm text-slate-700 truncate">{student.name}</p>
                    </div>
                    <div className="flex-1 h-8 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                        style={{
                          width: `${Math.max(pct, 5)}%`,
                          background: `linear-gradient(90deg, #3B82F6, ${student.avg_wpm > 30 ? '#22C55E' : student.avg_wpm > 15 ? '#F97316' : '#EF4444'})`,
                        }}
                      >
                        <span className="font-fredoka font-bold text-xs text-white drop-shadow-sm">
                          {student.avg_wpm}
                        </span>
                      </div>
                    </div>
                    <div className="w-16 text-right flex-shrink-0">
                      <span className="font-nunito text-xs text-slate-400">{student.sessions_count} sess.</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timeline for each student */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 md:p-6 shadow-md">
            <h2 className="font-fredoka font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-green-500" />
              Progression dans le temps
            </h2>
            <div className="space-y-4">
              {students.filter((s) => s.timeline.length > 1).map((student) => (
                <div key={student.id} className="border-b border-slate-100 pb-4 last:border-0">
                  <p className="font-nunito font-bold text-sm text-slate-700 mb-2">{student.name}</p>
                  <div className="flex items-end gap-1 h-16">
                    {student.timeline.map((point, i) => {
                      const maxT = Math.max(...student.timeline.map((t) => t.wpm), 1);
                      const h = (point.wpm / maxT) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center" title={`${point.text}: ${point.wpm} mots/min`}>
                          <div
                            className="w-full rounded-t-sm transition-all"
                            style={{
                              height: `${Math.max(h, 8)}%`,
                              backgroundColor: i === student.timeline.length - 1 ? '#22C55E' : '#93C5FD',
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="font-nunito text-[9px] text-slate-400">Debut</span>
                    <span className="font-nunito text-[9px] text-slate-400">Dernier</span>
                  </div>
                </div>
              ))}
              {students.every((s) => s.timeline.length <= 1) && (
                <p className="font-nunito text-sm text-slate-400 italic">Au moins 2 sessions par eleve sont necessaires pour voir la progression.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
